import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, Inbox } from 'lucide-react';
import {
  useInbox,
  useMarkAnnouncementRead,
  type InboxAnnouncement,
} from '../lib/api/announcements';
import { usePartner, useUser } from '../lib/api/useUser';
import { cn } from '../lib/cn';
import { resolvePersona } from '../lib/personas';

/**
 * InboxPage — guest PWA Updates surface (Phase 5c sub-commit 4,
 * 13 Jun 2026).
 *
 * Shows announcements from the partner the guest is attributed to.
 * Read state updates automatically when an unread card dwells in the
 * viewport for ≥ DWELL_MS via IntersectionObserver — gates against
 * scroll-through. Premium over "mark all read on page open" which
 * doesn't reflect actual reading intent.
 *
 * Deep-link via /inbox?focus=<id> (notification-tap landing path —
 * the server's push payload `url` falls back to this in sub-commit 6)
 * scrolls the target card into view and marks it read immediately
 * on mount.
 *
 * Mark-read uses the optimistic-update hook
 * (useMarkAnnouncementRead) — see src/lib/api/announcements.ts for
 * the full optimistic snapshot/rollback rationale. Visual change to
 * the row + bell-badge update (on MePage, once sub-commit 5 lands)
 * is instant; no network-round-trip flicker.
 *
 * Structure mirrors SettingsPage:
 *   - <Helmet> with noindex,nofollow
 *   - fixed inset-0 flex flex-col bg-cream-50
 *   - inline header chrome (no shared AppHeader in this repo)
 *   - safe-area-inset-top + bottom
 *   - container-narrow inside main
 *   - moor/cream palette tokens
 *   - back arrow to /me as the persistent escape hatch
 *
 * Empty / loading / error states branch inside <main>; the header
 * always renders so the back arrow never disappears mid-render.
 *
 * Accessibility:
 *   - <article role-implicit> per card with aria-labelledby pointing
 *     at the h2 title
 *   - sr-only "Unread" label conveys read/unread state to assistive
 *     tech (visual cues alone — bold + dot — aren't announced)
 *   - urlTarget link inherits its accessible name from the button text
 *   - external link rel="noopener noreferrer" hygiene
 */

/** Dwell threshold before IntersectionObserver triggers mark-read.
 *  500ms is long enough to gate scroll-through, short enough that a
 *  user actually reading the card doesn't notice the delay between
 *  view and visual read-state flip. */
const DWELL_MS = 500;

export default function InboxPage() {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const { t, i18n } = useTranslation('inbox');

  const { data: user, isLoading: userLoading, isError: userError } = useUser();
  const { data: partner } = usePartner();
  const { data, isLoading, isError, refetch } = useInbox();
  const markRead = useMarkAnnouncementRead();

  const persona = useMemo(
    () => (user ? resolvePersona(user.townSlug, partner ?? null) : null),
    [user, partner],
  );

  // Brief blank frame while the user record resolves (mirrors
  // SettingsPage). RequireAuth gates this route, so by the time we
  // mount the query is typically warm and this branch is short.
  if (userLoading) return null;
  if (userError || !user || !persona) {
    return <Navigate to="/" replace />;
  }

  const partnerName = partner?.name;
  const headerLabel = partnerName ?? `${persona.townLabel} Insider`;
  const heading = partnerName
    ? t('headingWithPartner', { partnerName })
    : t('heading');
  const announcements = data?.announcements ?? [];
  const lang = i18n.resolvedLanguage ?? 'en';

  return (
    <>
      <Helmet>
        <title>{`${t('pageTitle')} \u00b7 ${headerLabel}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="fixed inset-0 flex flex-col bg-cream-50">
        <header
          className="border-b border-moor-100 bg-cream-50/95 backdrop-blur"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="container-narrow flex items-center gap-3 px-4 py-3">
            <Link
              to="/me"
              aria-label={t('backToChat')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-moor-700 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wide text-moor-500">
                {headerLabel}
              </p>
              <h1 className="truncate text-base font-semibold text-moor-900">
                {heading}
              </h1>
            </div>
          </div>
        </header>
        <main
          id="main-content"
          className="min-h-0 flex-1 overflow-y-auto"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="container-narrow space-y-3 px-4 py-6">
            <InboxBody
              isLoading={isLoading}
              isError={isError}
              announcements={announcements}
              focusId={focusId}
              lang={lang}
              onMarkRead={markRead.mutate}
              onRetry={() => void refetch()}
              t={t}
            />
          </div>
        </main>
      </div>
    </>
  );
}

interface InboxBodyProps {
  isLoading: boolean;
  isError: boolean;
  announcements: InboxAnnouncement[];
  focusId: string | null;
  lang: string;
  onMarkRead: (id: string) => void;
  onRetry: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function InboxBody({
  isLoading,
  isError,
  announcements,
  focusId,
  lang,
  onMarkRead,
  onRetry,
  t,
}: InboxBodyProps) {
  if (isLoading) {
    return (
      <p
        role="status"
        aria-live="polite"
        className="py-12 text-center text-sm text-moor-500"
      >
        {t('loading')}
      </p>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 bg-red-50 p-4 text-center"
      >
        <p className="text-sm font-medium text-red-900">{t('errorTitle')}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 rounded-full bg-red-700 px-4 py-1.5 text-sm font-medium text-cream-50 transition-colors hover:bg-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 focus-visible:ring-offset-2"
        >
          {t('errorRetry')}
        </button>
      </div>
    );
  }
  if (announcements.length === 0) {
    return (
      <div className="py-12 text-center">
        <Inbox
          className="mx-auto h-10 w-10 text-moor-300"
          aria-hidden="true"
        />
        <p className="mt-4 text-base font-semibold text-moor-900">
          {t('emptyTitle')}
        </p>
        <p className="mt-1 text-sm text-moor-600">{t('emptyBody')}</p>
      </div>
    );
  }
  return (
    <>
      {announcements.map((a) => (
        <AnnouncementCard
          key={a.id}
          announcement={a}
          isFocused={a.id === focusId}
          lang={lang}
          onMarkRead={onMarkRead}
          t={t}
        />
      ))}
    </>
  );
}

interface AnnouncementCardProps {
  announcement: InboxAnnouncement;
  isFocused: boolean;
  lang: string;
  onMarkRead: (id: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function AnnouncementCard({
  announcement,
  isFocused,
  lang,
  onMarkRead,
  t,
}: AnnouncementCardProps) {
  const ref = useRef<HTMLElement | null>(null);
  const { id, title, body, urlTarget, createdAt, isRead } = announcement;
  const titleId = `announcement-${id}-title`;

  // ?focus=<id>: scroll the matching card into view + mark read
  // immediately on mount. Mount-only intentional — re-running on
  // isRead flip would re-scroll after the optimistic update fires,
  // which would yank the viewport while the user is scrolling.
  useEffect(() => {
    if (!isFocused) return;
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    if (!isRead) {
      onMarkRead(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  // Dwell-based mark-read. Observe only while unread; the effect
  // unsubscribes (cleanup → disconnect) as soon as the optimistic
  // update flips isRead → true via the dep array.
  useEffect(() => {
    if (isRead) return;
    const el = ref.current;
    if (el === null) return;
    if (typeof IntersectionObserver === 'undefined') return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          dwellTimer = setTimeout(() => {
            onMarkRead(id);
          }, DWELL_MS);
        } else if (dwellTimer !== null) {
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (dwellTimer !== null) clearTimeout(dwellTimer);
    };
  }, [id, isRead, onMarkRead]);

  return (
    <article
      ref={ref}
      aria-labelledby={titleId}
      data-testid={`announcement-${id}`}
      className={cn(
        'rounded-xl p-4 ring-1 transition-colors',
        isRead
          ? 'bg-cream-50 ring-moor-100'
          : 'bg-cream-100 ring-moor-300',
      )}
    >
      {!isRead && (
        <span className="sr-only">{t('unreadStateLabel')}. </span>
      )}
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className={cn(
            'mt-1.5 h-2 w-2 flex-none rounded-full',
            isRead ? 'bg-transparent' : 'bg-moor-700',
          )}
        />
        <div className="min-w-0 flex-1">
          <h2
            id={titleId}
            className={cn(
              'text-base text-moor-900',
              isRead ? 'font-normal' : 'font-semibold',
            )}
          >
            {title}
          </h2>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-moor-700">
            {body}
          </p>
          {urlTarget !== null ? (
            <a
              href={urlTarget}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-moor-700 px-3.5 py-1.5 text-sm font-medium text-cream-50 transition-colors hover:bg-moor-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              {t('openLink')}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : null}
          <p className="mt-3 text-xs uppercase tracking-wide text-moor-500">
            {formatRelativeTime(createdAt, lang, t)}
          </p>
        </div>
      </div>
    </article>
  );
}

/**
 * Relative-time formatter using Intl.RelativeTimeFormat for proper
 * locale-aware grammar across all 4 supported languages — "yesterday"
 * / "il y a 2 jours" / "vor 3 Tagen" / "hace 5 d\u00edas" without
 * needing per-language plural strings in the inbox namespace.
 * Sub-minute window falls back to the i18n'd timeAgoJustNow string
 * since RTF.format(-30, 'second') reads as "30 seconds ago" which is
 * too precise for a notification timestamp.
 */
function formatRelativeTime(
  iso: string,
  lang: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const now = new Date();
  const diffSec = (now.getTime() - then.getTime()) / 1000;
  if (diffSec < 60) return t('timeAgoJustNow');
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (diffSec < 3600) {
    return rtf.format(-Math.floor(diffSec / 60), 'minute');
  }
  if (diffSec < 86400) {
    return rtf.format(-Math.floor(diffSec / 3600), 'hour');
  }
  if (diffSec < 86400 * 7) {
    return rtf.format(-Math.floor(diffSec / 86400), 'day');
  }
  return rtf.format(-Math.floor(diffSec / (86400 * 7)), 'week');
}
